import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Icon, Input, Menu, Text } from '@pukka/ui-components';
import { twMerge } from 'tailwind-merge';
import { useSnapshot } from 'valtio';

import { BrandsPersistedState, BrandsState } from '../Brands.state';
import { BrandItemsModal, BrandsAssignModal, BrandsDeleteModal } from '../Modal';
import { BrandsAssignState } from '../Modal/BrandsAssign.state';

import { BrandsFormState } from './BrandsForm.state';

function BrandsForm({
  state = BrandsState,
  formState = BrandsFormState,
  assignState = BrandsAssignState,
  persistedState = BrandsPersistedState,
}: {
  state?: typeof BrandsState;
  formState?: typeof BrandsFormState;
  assignState?: typeof BrandsAssignState;
  persistedState?: typeof BrandsPersistedState;
}) {
  const [openAssignModal, setOpenAssignModal] = useState(false);
  const [openItemsModal, setOpenItemsModal] = useState(false);
  const {
    isOpen,
    entityType,
    type,
    parentName,
    brandName,
    isSaveDisabled,
    selectedPrincipal,
    brandItemCounts,
    nameError,
    brandItemNames,
    brandList,
    isLoading,
  } = useSnapshot(formState) as typeof BrandsFormState;
  const { selectedOutlet } = useSnapshot(state);
  const { showDeleteModal, showDeleteAllOutletModal } = useSnapshot(formState);
  const { outlets } = useSnapshot(persistedState) as typeof BrandsPersistedState;
  const {
    handleCloseForm,
    handleClickSave,
    handleClickSaveToAllOutlet,
    handleToggleDeleteModal,
    handleClickDelete,
    handleToggleDeleteAllOutletModal,
  } = formState;

  const { t } = useTranslation('library');
  const tlCommonPrefix = 'brands.form.common';
  const tlPrefix = `brands.form.${entityType}`;

  const assignModalType = entityType === 'parent' ? 'brand' : 'parent';
  const tlAssignPrefix = `brands.assignModal.${assignModalType}`;

  const nameValue = entityType === 'parent' ? parentName : brandName;

  const hasManyOutlets = outlets.length > 1;

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    formState.handleChangeName(value, entityType!);
  };

  const onClickOpenAssignModal = async () => {
    if (entityType === 'brand') {
      assignState.initParentList(formState.selectedPrincipal, () => setOpenAssignModal(true));
    } else {
      assignState.initBrandList(() => setOpenAssignModal(true));
    }
  };

  const handleSubmitAssign = () => {
    formState.handleClickAssign();

    setOpenAssignModal(false);
  };

  const hasPrincipalSelected = selectedPrincipal?.id && selectedPrincipal.id !== -1;

  useEffect(() => {
    if (isOpen) {
      const input = document.getElementById('name-input') as HTMLInputElement;

      setTimeout(() => {
        if (type === 'create') {
          input?.focus();
        }
      }, 500);
    }
  }, [isOpen, type]);

  return isOpen ? (
    <div id="brands-form" className="absolute inset-0 z-10 bg-white">
      <div className="flex justify-between px-8 pb-6 pt-8">
        <Button size="l" icon="close" variant="nude-blue" onClick={handleCloseForm} />
        <div className="flex gap-2">
          {type === 'edit' && !hasManyOutlets && (
            <Button
              size="l"
              variant="nude-red"
              icon="delete"
              disabled={isLoading}
              className="!border-red-500"
              onClick={handleToggleDeleteModal}
            >
              {t(`${tlCommonPrefix}.deleteButton`)}
            </Button>
          )}

          {type === 'edit' && hasManyOutlets && (
            <Menu
              align="end"
              trigger={
                <Button size="l" variant="nude-red" icon="delete" className="!border-red-500">
                  {t(`${tlCommonPrefix}.deleteButton`)}
                  <Icon icon="chevron-down" size="lg" className="ml-auto" />
                </Button>
              }
            >
              <Menu.Option className="mt-2 text-red-500" onClick={handleToggleDeleteModal}>
                <Icon icon="delete" size="sm" /> {t(`${tlCommonPrefix}.deleteButton`)}
              </Menu.Option>
              <Menu.Option className="mb-2 text-red-500" onClick={handleToggleDeleteAllOutletModal}>
                <Icon icon="delete" size="sm" /> {t(`${tlCommonPrefix}.deleteAllOutlet.${entityType}`)}
              </Menu.Option>
            </Menu>
          )}

          {hasManyOutlets && (
            <Button size="l" variant="secondary" disabled={isSaveDisabled || isLoading} onClick={handleClickSaveToAllOutlet}>
              {t(`${tlCommonPrefix}.saveToAllOutlet`)}
            </Button>
          )}

          <Button size="l" disabled={isSaveDisabled || isLoading} onClick={handleClickSave}>
            {t(`${tlCommonPrefix}.saveButton`)}
          </Button>
        </div>
      </div>
      <div className="px-8 md:mx-32">
        <Text variant="heading-2" weight="semibold" className="pb-6 pt-8">
          {t(`${tlPrefix}.title.${type}`)}
        </Text>
        <div className="py-3">
          <Text variant="title-2" className="mb-2">
            {t(`${tlPrefix}.detailsTitle.${type}`)}
          </Text>
          <Text variant="caption-1">
            {t(`${tlPrefix}.detailsDescription`)}{' '}
            <Text as="a" href="https://help.mokapos.com/s/?language=in" target="_blank" className="underline" color="blue">
              {t(`${tlPrefix}.detailsLink`)}
            </Text>
          </Text>
        </div>

        <div className={twMerge('py-3', entityType === 'parent' ? 'border-b' : '')}>
          <Text variant="body-2" className="mb-2 text-neutral-700">
            {t(`${tlPrefix}.nameLabel`)}
          </Text>
          <Input
            placeholder={t(`${tlPrefix}.namePlaceholder`)}
            className={twMerge('max-w-80', nameError ? 'border-red-500' : '')}
            maxLength={50}
            value={nameValue}
            onChange={handleNameChange}
            data-testid="name-input"
            id="name-input"
          />
          <Text variant="caption-1" className="mt-2 text-red-500">
            {nameError}
          </Text>
        </div>

        {entityType === 'brand' && (
          <div className="flex items-center justify-between border-b py-3">
            <div>
              <Text variant="body-2">{t(`${tlPrefix}.selectorTitle`)}</Text>
              <Text variant="caption-2" className="text-neutral-600">
                {hasPrincipalSelected ? selectedPrincipal.name : t(`${tlPrefix}.selectorDescription`)}
              </Text>
            </div>
            <Button size="s" variant="secondary" className="w-20" data-testid="assign-parent" onClick={onClickOpenAssignModal}>
              {hasPrincipalSelected ? t(`${tlCommonPrefix}.editButton`) : t('brands.assignButton')}
            </Button>
          </div>
        )}

        <div className="py-3">
          <Text variant="title-2" className="mb-2">
            {t(`${tlPrefix}.childTitle`)}
          </Text>
          {entityType === 'brand' && type === 'edit' && brandItemCounts > 0 ? (
            <Text variant="caption-1">{t(`${tlPrefix}.childDescription2`)}</Text>
          ) : (
            <Text variant="caption-1">{t(`${tlPrefix}.childDescription`)}</Text>
          )}
        </div>

        {entityType === 'parent' && (
          <div className="flex items-center justify-between border-b py-3">
            <div className="truncate">
              <Text variant="body-2">{t(`${tlPrefix}.selectorTitle`)}</Text>

              <Text variant="caption-2" className="truncate text-neutral-600">
                {brandList.length > 0
                  ? t(`${tlPrefix}.brandsAssigned`, {
                      count: brandList.length,
                    })
                  : t(`${tlPrefix}.selectorDescription`)}
              </Text>
              {brandList.length > 0 && (
                <Text variant="caption-2" className="truncate text-neutral-400">
                  {brandList
                    .map((brand) => brand.name)
                    .sort((a, b) => a.localeCompare(b))
                    .join(' • ')}
                </Text>
              )}
            </div>
            <div>
              <Button size="s" variant="secondary" className="w-20" data-testid="assign-brand" onClick={onClickOpenAssignModal}>
                {brandList.length > 0 ? t(`${tlCommonPrefix}.editButton`) : t('brands.assignButton')}
              </Button>
            </div>
          </div>
        )}

        {entityType === 'brand' && type === 'edit' && brandItemCounts > 0 && (
          <div className="flex items-center justify-between gap-4 border-b py-3">
            <div className="truncate">
              <Text variant="body-2">{t(`${tlPrefix}.itemList`)}</Text>
              <Text variant="caption-2" className="truncate text-neutral-600">
                {t(`${tlPrefix}.itemsAssigned`, {
                  count: brandItemCounts,
                })}
              </Text>
              <Text variant="caption-2" className="truncate text-neutral-400">
                {brandItemNames.join(' • ')}
              </Text>
            </div>
            <div>
              <Button size="s" variant="secondary" className="w-32" onClick={() => setOpenItemsModal(true)}>
                {t(`${tlCommonPrefix}.viewMoreButton`)}
              </Button>
            </div>
          </div>
        )}
      </div>

      <BrandsAssignModal
        open={openAssignModal}
        onToggleModal={setOpenAssignModal}
        heading={t(`${tlAssignPrefix}.title`)}
        description={t(`${tlAssignPrefix}.description`, { count: brandList.length })}
        searchPlaceholder={t(`${tlAssignPrefix}.searchPlaceholder`)}
        variant={assignModalType}
        state={assignState}
        onSubmit={handleSubmitAssign}
      />

      <BrandItemsModal
        open={openItemsModal}
        onToggleModal={setOpenItemsModal}
        brandName={brandName}
        outletName={selectedOutlet.value}
        itemList={brandItemNames}
      />

      <BrandsDeleteModal
        open={showDeleteModal}
        isLoading={isLoading}
        variant="single"
        onToggle={handleToggleDeleteModal}
        onClickDelete={handleClickDelete}
      />

      <BrandsDeleteModal
        open={showDeleteAllOutletModal}
        isLoading={isLoading}
        variant="all_outlet"
        onToggle={handleToggleDeleteAllOutletModal}
        onClickDelete={handleClickDelete}
      />
    </div>
  ) : null;
}

export { BrandsForm };
